import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppBar } from "../../components/AppShell";
import { useToast } from "../../components/Toast";
import { downloadBlob } from "../../lib/api";
import type { AttemptResult } from "../../lib/types";

interface StoredResult {
  result: AttemptResult;
  courseId: string;
  examTitle: string;
}

export function ExamResult() {
  const toast = useToast();
  const [data, setData] = useState<StoredResult | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("mc.last_result");
    if (raw) setData(JSON.parse(raw));
    return () => sessionStorage.removeItem("mc.last_result");
  }, []);

  if (!data) {
    return (
      <>
        <AppBar crumbs={[{ label: "Tabloul meu", to: "/student/dashboard" }, { label: "Rezultat examen" }]} />
        <div className="content">
          <div className="card text-muted">
            Nu există un rezultat recent. <Link to="/student/dashboard">Înapoi la tabloul meu</Link>.
          </div>
        </div>
      </>
    );
  }

  const { result, courseId, examTitle } = data;
  const passed = result.passed;
  const score = Number(result.score).toFixed(1);
  const min = Math.floor(result.time_spent_seconds / 60);
  const sec = result.time_spent_seconds % 60;
  const timeStr = `${min}:${String(sec).padStart(2, "0")}`;

  async function downloadCert() {
    try {
      const blob = await downloadBlob(`/me/certificates/${result.attempt_id}.pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificat-${result.attempt_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast("Certificat descărcat", "success");
    } catch (err) {
      toast(err instanceof Error ? err.message : "Eroare la descărcare", "error");
    }
  }

  return (
    <>
      <AppBar crumbs={[{ label: "Tabloul meu", to: "/student/dashboard" }, { label: "Rezultat examen" }]} />
      <div className="content">
        <div className="exam-shell">
          <div style={{
            color: "#fff",
            borderRadius: 24,
            padding: 40,
            textAlign: "center",
            marginBottom: 24,
            background: passed ? "linear-gradient(135deg, #14a36a, #0a6e47)" : "linear-gradient(135deg, #f61946, #a8092b)",
            boxShadow: "var(--shadow-lg)",
          }}>
            <i className={"fa-solid " + (passed ? "fa-trophy" : "fa-circle-xmark")} style={{ fontSize: "2rem", opacity: 0.9, marginBottom: 10 }}></i>
            <h1 style={{ color: "#fff", fontSize: "2.4rem" }}>{passed ? "Felicitări, ai promovat!" : "Mai ai puțin de lucru"}</h1>
            <p style={{ color: "rgba(255,255,255,.92)", maxWidth: 520, margin: "0 auto 28px" }}>
              {passed
                ? `Ai finalizat cu succes „${examTitle}". Certificatul este în pregătire.`
                : `Ai obținut ${score} / 10 la „${examTitle}". Revino la lecții și mai încearcă o dată.`}
            </p>
            <div style={{ width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "4px solid rgba(255,255,255,.4)", margin: "0 auto 20px", display: "grid", placeItems: "center" }}>
              <div>
                <strong style={{ fontSize: "3rem", fontWeight: 800 }}>{score}</strong>
                <small style={{ display: "block", opacity: 0.85 }}>din 10</small>
              </div>
            </div>
            <div className="flex gap-12" style={{ justifyContent: "center", flexWrap: "wrap" }}>
              {passed ? (
                <button className="btn btn-primary btn-lg" style={{ background: "#fff", color: "#0a6e47" }} onClick={downloadCert}>
                  <i className="fa-solid fa-download"></i> Descarcă certificatul
                </button>
              ) : (
                <Link to={`/student/curs/${courseId}`} className="btn btn-primary btn-lg" style={{ background: "#fff", color: "#a8092b" }}>
                  Revino la lecții
                </Link>
              )}
              <Link to="/student/dashboard" className="btn btn-ghost btn-lg" style={{ borderColor: "rgba(255,255,255,.4)", color: "#fff" }}>
                Tabloul meu
              </Link>
            </div>
          </div>

          <div className="grid-3" style={{ marginBottom: 24 }}>
            <div className="kpi"><div className="kpi-label">Răspunsuri corecte</div><div className="kpi-value" style={{ color: "var(--c-success)" }}>{result.correct}</div><div className="kpi-delta">din {result.total_questions}</div><div className="kpi-icon" style={{ background: "#e6f6ee", color: "#0b6b46" }}><i className="fa-solid fa-check"></i></div></div>
            <div className="kpi"><div className="kpi-label">Răspunsuri greșite</div><div className="kpi-value" style={{ color: "var(--c-accent)" }}>{result.total_questions - result.correct}</div><div className="kpi-delta">din {result.total_questions}</div><div className="kpi-icon" style={{ background: "var(--c-accent-soft)", color: "var(--c-accent)" }}><i className="fa-solid fa-xmark"></i></div></div>
            <div className="kpi"><div className="kpi-label">Timp utilizat</div><div className="kpi-value">{timeStr}</div><div className="kpi-icon"><i className="fa-regular fa-clock"></i></div></div>
          </div>
        </div>
      </div>
    </>
  );
}
